// interfaces
import type { InitSuccessPayload } from '@makemydeal/dr-dash-bff-types';

// externals
import { RSAA } from 'redux-api-middleware';

// libraries
import type { FSA, SimpleFSA, FSAWithMeta } from '@makemydeal/dr-browser-utils';
import type { RSAACallAction, RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import type { BffApiUrlResult } from '@makemydeal/dr-shared-ui-utils';
import { apiConfigHelper, locHrefUtil, offerSessionUtils, getDocumentReferrer } from '@makemydeal/dr-shared-ui-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { BootstrapResponseMeta } from '@makemydeal/dr-shared-types';
import { setCustomAttribute } from '@makemydeal/dr-activities-common';

// consts/enums
import { APPLICATION_LOAD_ERROR } from '../actionTypes/applicationLoadErrorActionTypes';
import type { BOOTSTRAP_FAILURE } from '../actionTypes/bootstrapActionTypes';
import { BOOTSTRAP_REQUEST, BOOTSTRAP_SUCCESS, BOOTSTRAP_INITIAL_LOAD } from '../actionTypes/bootstrapActionTypes';

// selectors
import { getSelectedOfferDealXgId, getSelectedOfferDealXgVersion } from '../selectors/selectedOfferSelectors';

// consts/enums
import type { INIT_PENCIL_SUCCESS, INIT_PENCIL_INVALID_DEAL } from '../actionTypes/initActionTypes';

/**
 * List of actions that are applicable for use in the reducer- add interfaces to the
 * AllFeatureToggleActions type by using the `|` character to separate them.
 * See `AllSharedInitAppActions` for a more complete example.
 */
export type AllFeatureToggleActions =
    | BootstrapAppInitSuccessAction
    | BootstrapAppInitPreSuccessAction
    | DealerDetailsFetchedSuccessAction;

export type AllSharedInitAppActions =
    | BootstrapInitialLoadAction
    | BootstrapAppAction
    | BootstrapSuccessAction
    | BootstrapFailureAction
    | BootstrapAppLoadErrorAction
    | BootstrapAppInitSuccessAction
    | BootstrapAppInitPreSuccessAction;

export type BootstrapAppLoadErrorPayload = {
    message: string;
};

export type DealerDetailsFetchedSuccessAction = FSA<'GET_DETAILS_SUCCESS', { featureToggles: any }>;

export type BootstrapAppInitSuccessAction = FSA<typeof INIT_PENCIL_SUCCESS, InitSuccessPayload>;

export type BootstrapAppInitPreSuccessAction = FSA<typeof INIT_PENCIL_INVALID_DEAL, InitSuccessPayload>;

export type BootstrapAppLoadErrorAction = FSA<typeof APPLICATION_LOAD_ERROR, BootstrapAppLoadErrorPayload>;

export type BootstrapAppAction = SimpleFSA<typeof BOOTSTRAP_REQUEST>;

export type BootstrapSuccessAction = FSA<typeof BOOTSTRAP_SUCCESS, InitSuccessPayload>;

export type BootstrapFailureAction = FSAWithMeta<typeof BOOTSTRAP_FAILURE, unknown, BootstrapResponseMeta>;

export type BootstrapInitialLoadAction = SimpleFSA<typeof BOOTSTRAP_INITIAL_LOAD>;

export const initialLoad = (): BootstrapInitialLoadAction => ({ type: BOOTSTRAP_INITIAL_LOAD });

export const fetchBootstrapData = (appPrefix: string, options?: any) => {
    const {
        withCurrentDealVersion,
        dealXgVersion,
        loadDraftScenario = false,
        updatePrevOffer = undefined,
        templateId,
        dealerId
    } = options || {};
    return (dispatch: any, getState: () => StateTree) => {
        const state = getState();
        dispatch(
            fetchBootstrapDataWorker(
                appPrefix,
                state,
                withCurrentDealVersion,
                dealXgVersion,
                loadDraftScenario,
                updatePrevOffer,
                templateId,
                dealerId
            )
        );
    };
};

export const fetchBootstrapDataWorker = (
    appPrefix: string,
    state: StateTree,
    withCurrentDealVersion?: boolean,
    dealExchangeVersion?: string,
    loadDraftScenario?: boolean,
    updatePrevOffer?: boolean,
    templateId?: string,
    dealerId?: string
) => {
    let bffApiUrlResult: BffApiUrlResult;

    const locHref = locHrefUtil.getLocHref();

    const dealXgId = getSelectedOfferDealXgId(state);
    const dealXgVersion = dealExchangeVersion || getSelectedOfferDealXgVersion(state);
    const commonOrgId = offerSessionUtils.getCommonOrgId();

    let url: URL;
    if (dealXgId) {
        // if we get dealXgID, then use it to load the offer.
        // if version is specified, provide it, otherwise we'll get the latest
        bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, 'initDealXg');
        const base = `${bffApiUrlResult.url}/${dealXgId}`;

        url = new URL(base);
        if (dealXgVersion) {
            url = new URL(`${base}/${dealXgVersion}`);
        }

        if (templateId) {
            url.searchParams.set('templateId', templateId);
        }

        if (dealXgVersion && withCurrentDealVersion) {
            url.searchParams.set('withCurrentDealVersion', 'true');
        }

        if (loadDraftScenario) {
            url.searchParams.set('loadDraftScenario', 'true');
        }
    } else if (commonOrgId) {
        bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, 'initOrg');
        url = new URL(`${bffApiUrlResult.url}/${encodeURIComponent(commonOrgId)}`);
        // A dealership chosen inside Manager View re-inits the org deal with the selected dealer.
        if (dealerId) {
            url.searchParams.set('dealerId', dealerId);
        }
    } else {
        // if we fall here it means we do not have the right values
        return {
            type: APPLICATION_LOAD_ERROR,
            payload: {
                message: '"dealXgId" or "OrgID" is not available.'
            }
        };
    }

    if (bffApiUrlResult.environmentName !== bffApiUrlResult.defaultEnvironmentName) {
        /* eslint-disable-next-line no-console */
        console.log(
            'OVERRIDE APPLIED: ' +
                `Environment ${bffApiUrlResult.defaultEnvironmentName} --> ${bffApiUrlResult.overriddenEnvironmentName}`
        );
    }

    const environmentType = offerSessionUtils.identifyEnvironment(bffApiUrlResult.environmentName);
    offerSessionUtils.setEnvironmentType(environmentType);

    const referrer = getDocumentReferrer();
    const requestStartTime = Date.now();

    const request: RSAACallRequest = {
        endpoint: url.toString(),
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'x-dr-referrer': referrer
        },
        types: [
            BOOTSTRAP_REQUEST,
            {
                type: BOOTSTRAP_SUCCESS,
                meta: (_action: any, _state: any, _res: any) => {
                    const duration = Date.now() - requestStartTime;
                    // helps filter metrics - browsers can throttle behavior when the app is hidden. values: 'visible' | 'hidden'
                    setCustomAttribute('bootstrapResultDocumentVisibilityState', document.visibilityState);
                    setCustomAttribute('bootstrapRequestDuration', duration);
                    setCustomAttribute('bootstrapRequestSuccess', true);
                    performance.mark('bootstrap-success-time');
                    return {
                        updatePrevOffer,
                        requestDuration: duration,
                        isVersionSpecificLoad: Boolean(dealXgVersion)
                    };
                }
            },
            {
                type: 'BOOTSTRAP_FAILURE',
                meta:
                    // this is RSAA standard code; we'll assert this coverage behavior in the reducer.
                    // istanbul ignore next
                    (_action: any, _state: any, res: any) => {
                        const duration = Date.now() - requestStartTime;
                        // helps filter metrics - browsers can throttle behavior when the app is hidden. values: 'visible' | 'hidden'
                        setCustomAttribute('bootstrapResultDocumentVisibilityState', document.visibilityState);
                        setCustomAttribute('bootstrapRequestDuration', duration);
                        setCustomAttribute('bootstrapRequestSuccess', false);
                        if (res) {
                            return {
                                status: res.status,
                                statusText: res.statusText,
                                requestDuration: duration
                            };
                        } else {
                            return {
                                statusText: 'Network request failed',
                                requestDuration: duration
                            };
                        }
                    }
            }
        ]
    };

    return {
        [RSAA]: request
    } as RSAACallAction;
};
